import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import ChatBar from "../../components/chat/ChatBar/ChatBar";
import ModalFeedback from "../../components/chat/ModalFeedback/ModalFeedback";
import ChatSidebar from "../../components/chat/ChatSidebar/ChatSidebar";
import ChatLog from "../../components/chat/ChatLog";
import { TypeFeedback } from "../../constants/TypeFeedback";
import { TypeSender } from "../../constants/TypeSender";
import api from "../../services/BackendServices";
import { formatDate } from "../../utils/date";
import { parseJwt } from "../../utils/parseJwt";
import logo from "../../assets/images/logoTrixie.png";

import "../../styles/styles.css";
import { useSelectedFile } from "../../context/SelectedFileContext";
import { useMenu } from "../../context/MenuContext";

interface ChatMessageType {
  sender: string;
  message: string;
  audioText?: string;
  questions?: string[];
  qa_id?: string;
}

const Chat: React.FC = () => {
  // Extraemos datos del usuario desde Redux
  const USER_ID: string = parseJwt(
    useSelector((state: any) => state.userProfile.token)
  ).id;
  const username: string =
    useSelector((state: any) => state.userProfile.realm)
      ?.charAt(0)
      ?.toUpperCase() +
    useSelector((state: any) => state.userProfile.realm)?.slice(1);

  // Estados
  const [chatLog, setChatLog] = useState<ChatMessageType[]>([]);
  const [utterances, setUtterances] = useState<{
    [key: string]: SpeechSynthesisUtterance | null;
  }>({});
  const [isPaused, setIsPaused] = useState<{ [key: string]: boolean }>({});
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedRates, setSelectedRates] = useState<number>(1.0);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [activeAudio, setActiveAudio] = useState<string | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState<{
    [key: string]: boolean;
  }>({});
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<{ [key: string]: string }>(
    {}
  );
  const [isLoadingResponse, setIsLoadingResponse] = useState<boolean>(false);
  const [showDownOptions, setShowDownOptions] = useState<{
    [key: string]: boolean;
  }>({});
  const { isMenuOpen, toggleMenu } = useMenu();

  const fetchWelcomeMessage = async () => {
    try {
      const response = await api.GeneralServices.fetchWelcomeMessage();
      const result = response.data;

      setChatLog((prevChatLog) => {
        if (prevChatLog.length === 0) {
          return [
            ...prevChatLog,
            { sender: TypeSender["tutor"], message: result.message },
          ];
        }
        return prevChatLog;
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.GeneralServices.fetchSessions(USER_ID);
      setSessions(response.data.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchChatHistory = async (sessionId: string) => {
    try {
      const response = await api.GeneralServices.fetchChatHistory(
        USER_ID,
        sessionId
      );
      const result = response.data;

      const newIsPaused: { [key: string]: boolean } = {};

      const formattedChatLog = result.data
        .map((chat: any) => {
          if (chat.Trixie) {
            newIsPaused[chat.Trixie] = true;
          }
          return [
            { sender: username, message: chat.prompt },
            {
              sender: TypeSender["tutor"],
              message: chat.Trixie,
              audioText: chat.Trixie,
              qa_id: chat.qa_id,
            },
          ];
        })
        .flat();

      setChatLog(formattedChatLog);
      setIsPaused(newIsPaused);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const addChatMessage = (
    sender: string,
    message: string,
    audioText: string = "",
    questions: string[] = [],
    qa_id: string | null = null
  ) => {
    setChatLog((prevChatLog) => [
      ...prevChatLog,
      {
        sender,
        message,
        audioText,
        questions,
        qa_id: qa_id ?? undefined, // Si qa_id es null, lo cambia por undefined
      },
    ]);
  };

  const handleReadFile = async (file: File) => {
    try {
      toast.info("Cargando archivo...");
      const response = await api.GeneralServices.readFile(file);
      const text = response.data.text;

      addChatMessage(
        TypeSender["tutor"],
        `Reproduciendo contenido del archivo "${file.name}"...`,
        text
      );

      readMessage(text, selectedRates, text);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ocurrió un error al procesar y leer el archivo.");
    }
  };

  const readMessage = (message: string, playbackRate: number, key: string) => {
    setActiveAudio(key);
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();

      setIsPaused((prev) => {
        const newPaused: { [key: string]: boolean } = {};
        Object.keys(prev).forEach((k) => {
          newPaused[k] = true;
        });
        return newPaused;
      });

      setIsPaused((prev) => ({ ...prev, [key]: false }));

      const newUtterance = new SpeechSynthesisUtterance(message);
      newUtterance.lang = "es-ES";
      newUtterance.rate = playbackRate;

      setUtterances((prev) => ({ ...prev, [key]: newUtterance }));
      window.speechSynthesis.speak(newUtterance);

      newUtterance.onend = () => {
        setIsPaused((prev) => ({ ...prev, [key]: true }));
        setUtterances((prev) => ({ ...prev, [key]: null }));
        setActiveAudio(null);
      };
    } else {
      toast.error("Tu navegador no soporta la síntesis de voz.");
    }
  };

  const adjustPlaybackRate = (rate: number, key: string) => {
    if (utterances[key]) {
      window.speechSynthesis.cancel();

      const newUtterance = new SpeechSynthesisUtterance(utterances[key]!.text);
      newUtterance.lang = "es-ES";
      newUtterance.rate = rate;

      setUtterances((prev) => ({ ...prev, [key]: newUtterance }));
      window.speechSynthesis.speak(newUtterance);

      setIsPaused((prev) => ({ ...prev, [key]: false }));
      setSelectedRates(rate);

      newUtterance.onend = () => {
        setIsPaused((prev) => ({ ...prev, [key]: true }));
        setUtterances((prev) => ({ ...prev, [key]: null }));
      };
    }
  };

  const pauseCurrentAudio = (key: string) => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused((prev) => ({ ...prev, [key]: true }));
    }
  };

  const resumeCurrentAudio = (key: string) => {
    window.speechSynthesis.resume();
    setIsPaused((prev) => ({ ...prev, [key]: false }));
  };

  const handleSessionClick = (session_id: string) => {
    window.speechSynthesis.cancel();
    setSelectedSession(session_id);
    fetchChatHistory(session_id);
    setActiveAudio(null);
    toggleMenu();
  };

  const createNewChat = async () => {
    const newSessionId = `session_${Date.now()}`;
    setSelectedSession(newSessionId);
    setChatLog([]);
    fetchWelcomeMessage();
  };

  const handleFeedback = async (message: any, type: string) => {
    if (type === TypeFeedback["up"]) {
      setLoadingFeedback((prev) => ({
        ...prev,
        [message.qa_id]: true,
      }));
      setShowDownOptions((prev) => ({ ...prev, [message.qa_id]: false }));

      api.GeneralServices.sendFeedback({
        qa_id: message.qa_id,
        correct_answer: "",
        was_util: 1,
      })
        .then(() => {
          setFeedbackGiven((prev) => ({ ...prev, [message.qa_id]: type }));
          setLoadingFeedback((prev) => ({ ...prev, [message.qa_id]: false }));
          toast.success("¡Gracias por tu feedback!");
        })
        .catch(() => {
          setLoadingFeedback((prev) => ({ ...prev, [message.qa_id]: false }));
          toast.error("Hubo un error al enviar tu feedback.");
        });
    } else if (type === TypeFeedback["down"]) {
      setShowDownOptions((prev) => ({
        ...prev,
        [message.qa_id]: !prev[message.qa_id],
      }));
      setSelectedMessage(message);
    }
  };

  const handleSubmitFeedback = async (detail: string) => {
    setShowFeedbackModal(false);
    setLoadingFeedback((prev) => ({
      ...prev,
      [selectedMessage.qa_id]: true,
    }));
    api.GeneralServices.sendFeedback({
      qa_id: selectedMessage.qa_id,
      correct_answer: detail,
      was_util: 0,
    })
      .then(() => {
        setShowDownOptions((prev) => ({
          ...prev,
          [selectedMessage.qa_id]: false,
        }));
        setFeedbackGiven((prev) => ({
          ...prev,
          [selectedMessage.qa_id]: TypeFeedback["down"],
        }));
        setLoadingFeedback((prev) => ({
          ...prev,
          [selectedMessage.qa_id]: false,
        }));
        toast.success("¡Gracias por tu feedback!");
      })
      .catch(() => {
        setLoadingFeedback((prev) => ({
          ...prev,
          [selectedMessage.qa_id]: false,
        }));
        toast.error("Hubo un error al enviar tu feedback.");
      });
  };

  useEffect(() => {
    fetchWelcomeMessage();
    fetchSessions();
    if (!selectedSession) {
      createNewChat();
    }
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.speechSynthesis.cancel();
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [selectedSession]);

  return (
    <div className="app">
      <div className="up-content">
        <ChatSidebar
          sessions={sessions}
          createNewChat={createNewChat}
          handleSessionClick={handleSessionClick}
          isMenuOpen={isMenuOpen}
          formatDate={formatDate}
        />
        <div className="main-content">
          <div className="chat-container">
            <ChatLog
              chatLog={chatLog}
              readMessage={readMessage}
              pauseCurrentAudio={pauseCurrentAudio}
              resumeCurrentAudio={resumeCurrentAudio}
              adjustPlaybackRate={adjustPlaybackRate}
              activeAudio={activeAudio}
              isPaused={isPaused}
              selectedRates={selectedRates}
              isSmallScreen={isSmallScreen}
              handleFeedback={handleFeedback}
              loadingFeedback={loadingFeedback}
              feedbackGiven={feedbackGiven}
              showDownOptions={showDownOptions}
              setShowDownOptions={setShowDownOptions}
              handleSubmitFeedback={handleSubmitFeedback}
              setShowFeedbackModal={setShowFeedbackModal}
              isLoadingResponse={isLoadingResponse}
            />
          </div>
          <ChatBar
            addChatMessage={addChatMessage}
            handleReadFile={handleReadFile}
            sessionId={selectedSession}
            fetchSessions={fetchSessions}
            isLoadingResponse={isLoadingResponse}
            setIsLoadingResponse={setIsLoadingResponse}
          />
        </div>
        {showFeedbackModal && (
          <ModalFeedback
            setShowFeedbackModal={setShowFeedbackModal}
            handleSubmitFeedback={handleSubmitFeedback}
          />
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Chat;
